import { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../services/api';
import styles from './ResourcesPage.module.css';

const ICON_MAP = {
  rocket: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polygon points="10 8 16 12 10 16 10 8" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  ),
  stethoscope: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  pill: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-6 18.75h9" />
    </svg>
  ),
  truck: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),
  'credit-card': (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  shield: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  wrench: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  ),
};

const getIcon = (iconName) => ICON_MAP[iconName] || (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const highlightMatch = (text, query) => {
  if (!query || !text) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className={styles.highlight}>{part}</mark>
      : part
  );
};

const formatVideoDuration = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return null;
  const totalSeconds = Math.round(value);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

const isPlayableVideo = (video) => Boolean(video?.videoUrl);

const ResourcesPage = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [resources, setResources] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchNonce, setSearchNonce] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [pdfResources, setPdfResources] = useState([]);
  const [pdfModal, setPdfModal] = useState(null);
  const [videoResources, setVideoResources] = useState([]);
  const [videoModal, setVideoModal] = useState(null);
  const [videoDurations, setVideoDurations] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadNonce, setLoadNonce] = useState(0);
  const [categoryError, setCategoryError] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [catData, pdfData, videoData] = await Promise.all([
          api.get('/resources/categories'),
          api.get('/resources?type=pdf'),
          api.get('/resources?type=video'),
        ]);
        setCategories(catData.categories || []);
        setPdfResources(pdfData.resources || []);
        setVideoResources(videoData.resources || []);
        setLoadError(null);
      } catch (err) {
        console.error('Failed to fetch resources data:', err);
        // Without this the hub keeps its empty arrays and renders as though the
        // knowledge base were genuinely empty — a failed load and an empty hub
        // were indistinguishable. Same reason `searchError` exists below.
        setLoadError(err?.message || 'Resources are temporarily unavailable.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [loadNonce]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await api.get(`/resources/search?q=${encodeURIComponent(searchQuery.trim())}`);
        setSearchResults(data.resources || []);
        setSearchError(null);
      } catch (err) {
        console.error('Search failed:', err);
        setSearchResults([]);
        setSearchError(err?.message || 'Search is temporarily unavailable.');
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, searchNonce]);

  const handleCategoryClick = async (category) => {
    setSelectedCategory(category);
    setSearchQuery('');
    setSearchResults([]);
    setExpandedFaq(null);
    setCategoryError(null);
    try {
      const data = await api.get(`/resources?categoryId=${category.id}&type=faq`);
      setResources(data.resources || []);
    } catch (err) {
      console.error('Failed to fetch resources:', err);
      // `resources` must be cleared too: leaving the previous category's
      // articles on screen under this category's heading is a second way to
      // show the user something that is not true.
      setResources([]);
      setCategoryError(err?.message || 'These articles are temporarily unavailable.');
    }
  };

  const handleBackToHub = () => {
    setSelectedCategory(null);
    setResources([]);
  };

  const handleNeedHelp = (message = 'I need help') => {
    window.Intercom?.('showNewMessage', message);
  };

  const handleVideoMetadata = (videoId, duration) => {
    const formattedDuration = formatVideoDuration(duration);
    if (!formattedDuration) return;
    setVideoDurations((durations) =>
      durations[videoId] === formattedDuration
        ? durations
        : { ...durations, [videoId]: formattedDuration }
    );
  };

  // Group search results by category
  const groupedResults = searchResults.reduce((groups, resource) => {
    const catId = resource.categoryId;
    const cat = categories.find((c) => c.id === catId);
    const catName = cat?.name || 'Other';
    if (!groups[catName]) groups[catName] = { category: cat, items: [] };
    groups[catName].items.push(resource);
    return groups;
  }, {});

  // The category drilldown fetches `type=faq` only, so the hub card's count must be
  // FAQ-only too — otherwise a category holding videos/PDFs advertises more articles
  // than clicking it can ever show. `type` is a closed set (faq|pdf|video), so
  // subtracting the non-FAQ resources already in state is exact and costs no request.
  const faqCountByCategory = useMemo(() => {
    const nonFaqByCategory = [...pdfResources, ...videoResources].reduce(
      (acc, { categoryId }) =>
        categoryId ? { ...acc, [categoryId]: (acc[categoryId] || 0) + 1 } : acc,
      {}
    );
    return categories.reduce(
      (acc, { id, resourceCount }) => ({
        ...acc,
        [id]: Math.max(0, (resourceCount || 0) - (nonFaqByCategory[id] || 0)),
      }),
      {}
    );
  }, [categories, pdfResources, videoResources]);

  const isShowingSearch = searchQuery.trim().length > 0;

  if (loading) {
    return <div className={styles.loading}>Loading resources...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.hero}>
        <h1 className={styles.heroTitle}>How can we help?</h1>
        <p className={styles.heroSubtitle}>
          Search our knowledge base or browse by category
        </p>
        <div className={styles.searchWrapper}>
          <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedCategory(null);
            }}
          />
          {searchQuery && (
            <button
              className={styles.searchClear}
              onClick={() => { setSearchQuery(''); setSearchResults([]); }}
              aria-label="Clear search"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {isShowingSearch ? (
        <div className={styles.searchResultsSection}>
          {isSearching ? (
            <p className={styles.searchStatus}>Searching...</p>
          ) : searchError ? (
            <div className={styles.emptySearch} role="alert">
              <p className={styles.emptySearchTitle}>Search is temporarily unavailable</p>
              <p className={styles.emptySearchSubtitle}>
                We could not run your search, so we cannot say what matches &quot;{searchQuery}&quot;. This is not a
                &quot;no results&quot; answer &mdash; please try again.
              </p>
              <div className={styles.needHelp}>
                <button className={styles.contactBtn} onClick={() => setSearchNonce((n) => n + 1)}>
                  Try again
                </button>
              </div>
            </div>
          ) : searchResults.length === 0 ? (
            <div className={styles.emptySearch}>
              <p className={styles.emptySearchTitle}>No results found for "{searchQuery}"</p>
              <p className={styles.emptySearchSubtitle}>Try different keywords or browse our categories below.</p>
              <div className={styles.needHelp}>
                <p>Need more help?</p>
                <button className={styles.contactBtn} onClick={() => handleNeedHelp('I need help')}>
                  Contact Support
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className={styles.searchStatus}>
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
              </p>
              {Object.entries(groupedResults).map(([catName, { items }]) => (
                <div key={catName} className={styles.searchGroup}>
                  <h3 className={styles.searchGroupTitle}>{catName}</h3>
                  <div className={styles.faqList}>
                    {items.map((resource) => (
                      <div key={resource.id} className={`${styles.faqItem} ${expandedFaq === resource.id ? styles.faqItemOpen : ''}`}>
                        <button
                          className={styles.faqHeader}
                          onClick={() => setExpandedFaq(expandedFaq === resource.id ? null : resource.id)}
                        >
                          <h3 className={styles.faqQuestion}>{highlightMatch(resource.title, searchQuery)}</h3>
                          <svg
                            className={`${styles.faqChevron} ${expandedFaq === resource.id ? styles.faqChevronOpen : ''}`}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                        <div className={`${styles.faqBody} ${expandedFaq === resource.id ? styles.faqBodyOpen : ''}`}>
                          <p className={styles.faqAnswer}>{highlightMatch(resource.content, searchQuery)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className={styles.needHelp}>
                <p>Need more help?</p>
                <button className={styles.contactBtn} onClick={() => handleNeedHelp('I need help')}>
                  Contact Support
                </button>
              </div>
            </>
          )}
        </div>
      ) : selectedCategory ? (
        <div className={styles.categoryDetail}>
          <button className={styles.backBtn} onClick={handleBackToHub}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Categories
          </button>
          <h2 className={styles.categoryTitle}>{selectedCategory.name}</h2>
          {selectedCategory.description && (
            <p className={styles.categoryDescription}>{selectedCategory.description}</p>
          )}
          <div className={styles.faqList}>
            {categoryError ? (
              <div className={styles.emptySearch} role="alert">
                <p className={styles.emptySearchTitle}>These articles could not be loaded</p>
                <p className={styles.emptySearchSubtitle}>
                  We could not load the articles in &quot;{selectedCategory.name}&quot;, so we cannot say what is in
                  it. This is not a &quot;no articles&quot; answer &mdash; please try again.
                </p>
                <div className={styles.needHelp}>
                  <button className={styles.contactBtn} onClick={() => handleCategoryClick(selectedCategory)}>
                    Try again
                  </button>
                </div>
              </div>
            ) : resources.length === 0 ? (
              <p className={styles.emptyState}>No articles in this category yet.</p>
            ) : (
              resources.map((resource) => (
                <div key={resource.id} className={`${styles.faqItem} ${expandedFaq === resource.id ? styles.faqItemOpen : ''}`}>
                  <button
                    className={styles.faqHeader}
                    onClick={() => setExpandedFaq(expandedFaq === resource.id ? null : resource.id)}
                  >
                    <h3 className={styles.faqQuestion}>{resource.title}</h3>
                    <svg
                      className={`${styles.faqChevron} ${expandedFaq === resource.id ? styles.faqChevronOpen : ''}`}
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <div className={`${styles.faqBody} ${expandedFaq === resource.id ? styles.faqBodyOpen : ''}`}>
                    <p className={styles.faqAnswer}>{resource.content}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className={styles.needHelp}>
            <p>Need more help?</p>
            <button className={styles.contactBtn} onClick={() => handleNeedHelp('I need help')}>
              Contact Support
            </button>
          </div>
        </div>
      ) : loadError ? (
        <div className={styles.emptySearch} role="alert">
          <p className={styles.emptySearchTitle}>Resources could not be loaded</p>
          <p className={styles.emptySearchSubtitle}>
            We could not load the knowledge base, so nothing is shown below. This is not an empty
            help centre &mdash; please check your connection and try again.
          </p>
          <div className={styles.needHelp}>
            <button className={styles.contactBtn} onClick={() => setLoadNonce((n) => n + 1)}>
              Try again
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.categoryGrid}>
            {categories.map((category) => (
              <button
                key={category.id}
                className={styles.categoryCard}
                onClick={() => handleCategoryClick(category)}
              >
                <div className={styles.categoryIconWrapper}>
                  {getIcon(category.icon)}
                </div>
                <h3 className={styles.categoryName}>{category.name}</h3>
                <p className={styles.categoryCount}>
                  {faqCountByCategory[category.id] || 0} article{(faqCountByCategory[category.id] || 0) !== 1 ? 's' : ''}
                </p>
              </button>
            ))}
          </div>



          {videoResources.length > 0 && (
            <div className={styles.videosSection}>
              <h2 className={styles.videosSectionTitle}>Videos</h2>
              <p className={styles.videosSectionSubtitle}>Tutorials, walkthroughs, and how-to guides</p>
              <div className={styles.videosGrid}>
                {videoResources.map((video) => {
                  const playable = isPlayableVideo(video);
                  const duration = videoDurations[video.id] || video.duration || video.durationLabel || 'Video';
                  return (
                    <button
                      key={video.id}
                      className={`${styles.videoCard} ${playable ? '' : styles.videoCardLocked}`}
                      onClick={() => playable && setVideoModal(video)}
                      aria-label={playable ? `Play ${video.title}` : `${video.title} coming soon`}
                    >
                      {!playable && (
                        <div className={styles.comingSoonOverlay}>
                          <span className={styles.comingSoonBadge}>Coming Soon</span>
                        </div>
                      )}
                      <div className={styles.videoThumb}>
                        {video.thumbnailUrl ? (
                          <img loading="lazy" src={video.thumbnailUrl} alt={video.title} className={styles.videoThumbImg} />
                        ) : (
                          <div className={styles.videoThumbPlaceholder} />
                        )}
                        {playable && (
                          <video
                            preload="metadata"
                            src={video.videoUrl}
                            className={styles.videoMetadata}
                            aria-hidden="true"
                            tabIndex="-1"
                            onLoadedMetadata={(event) => handleVideoMetadata(video.id, event.currentTarget.duration)}
                          />
                        )}
                        <div className={styles.videoPlayOverlay}>
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="white">
                            <polygon points="5 3 19 12 5 21 5 3" />
                          </svg>
                        </div>
                        <span className={styles.videoDuration}>{duration}</span>
                      </div>
                      <div className={styles.videoInfo}>
                        <h3 className={styles.videoTitle}>{video.title}</h3>
                        {video.content && <p className={styles.videoDesc}>{video.content}</p>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {pdfResources.length > 0 && (
            <div className={styles.downloadsSection}>
              <h2 className={styles.downloadsSectionTitle}>Downloads</h2>
              <p className={styles.downloadsSectionSubtitle}>Guides, tips, and medication information</p>
              <div className={styles.downloadsGrid}>
                {pdfResources.map((pdf) => (
                  <div
                    key={pdf.id}
                    className={styles.downloadCard}
                    style={pdf.comingSoon ? { position: 'relative', opacity: 0.6, filter: 'grayscale(0.5)' } : {}}
                  >
                    {pdf.comingSoon && (
                      <div style={{
                        position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '12px', backdropFilter: 'blur(2px)', cursor: 'not-allowed'
                      }}
                        onClick={(e) => { e.stopPropagation(); alert('Coming Soon'); }}
                      >
                        <span style={{
                          padding: '8px 20px', borderRadius: '20px', fontSize: '14px', fontWeight: 700, letterSpacing: '0.5px',
                          color: '#fff', backgroundColor: 'rgba(245,158,11,0.9)', textTransform: 'uppercase'
                        }}>Coming Soon</span>
                      </div>
                    )}
                    <div
                      className={styles.downloadCardBody}
                      onClick={() => !pdf.comingSoon && setPdfModal(pdf)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && !pdf.comingSoon && setPdfModal(pdf)}
                      style={pdf.comingSoon ? { cursor: 'not-allowed' } : {}}
                    >
                      <div className={styles.downloadIconWrapper}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                      </div>
                      <div className={styles.downloadInfo}>
                        <h3 className={styles.downloadTitle}>{pdf.title}</h3>
                        {pdf.content && <p className={styles.downloadDesc}>{pdf.content}</p>}
                        <span className={styles.viewHint}>{pdf.comingSoon ? 'Coming Soon' : 'Click to preview'}</span>
                      </div>
                    </div>
                    {pdf.comingSoon ? (
                      <button
                        className={styles.downloadBtn}
                        onClick={(e) => { e.stopPropagation(); alert('Coming Soon'); }}
                        style={{ cursor: 'not-allowed', opacity: 0.5 }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download
                      </button>
                    ) : (
                      <a
                        href={pdf.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className={styles.downloadBtn}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {pdfModal && (
        <div className={styles.pdfModalOverlay} onClick={() => setPdfModal(null)}>
          <div className={styles.pdfModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.pdfModalHeader}>
              <h3 className={styles.pdfModalTitle}>{pdfModal.title}</h3>
              <div className={styles.pdfModalActions}>
                <a
                  href={pdfModal.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className={styles.pdfModalDownload}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download
                </a>
                <button className={styles.pdfModalClose} onClick={() => setPdfModal(null)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            <iframe
              src={pdfModal.pdfUrl}
              className={styles.pdfIframe}
              title={pdfModal.title}
            />
          </div>
        </div>
      )}

      {videoModal && (
        <div className={styles.videoModalOverlay} onClick={() => setVideoModal(null)}>
          <div className={styles.videoModalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.videoModalClose} onClick={() => setVideoModal(null)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className={styles.videoPlayer}>
              <video
                src={videoModal.videoUrl}
                controls
                autoPlay
                className={styles.videoElement}
              />
            </div>
            <h3 className={styles.videoModalTitle}>{videoModal.title}</h3>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesPage;
